const readAsDataURL = (blob: Blob) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });

const loadImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () =>
            reject(new Error('That image could not be read.'));
        image.src = src;
    });

// Scales an image down so its longest side is at most maxSize pixels and
// returns it as a data URL. Smaller images come back unchanged. The server
// stores thumbnails as uploaded, so a full-screen screenshot would otherwise
// be kept (and downloaded by every viewer) at full resolution.
export default async function resizeImage(
    blob: Blob,
    maxSize = 1024
): Promise<string> {
    const dataURL = await readAsDataURL(blob);
    const image = await loadImage(dataURL);

    const scale = maxSize / Math.max(image.naturalWidth, image.naturalHeight);
    if (scale >= 1) return dataURL;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.naturalWidth * scale);
    canvas.height = Math.round(image.naturalHeight * scale);

    const context = canvas.getContext('2d');
    if (!context) return dataURL;

    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    return blob.type === 'image/jpeg'
        ? canvas.toDataURL('image/jpeg', 0.9)
        : canvas.toDataURL('image/png');
}
